import classNames from "classnames"
import { BackgroundDesignator, BorderShape } from "components/DesignSystem"
import { PaperBox } from "components/PaperBox/PaperBox"
import { PropsWithChildren } from "react"

/**
 * Preset inner-padding steps for `Paper`. Each maps to a fixed Tailwind
 * padding utility so callers control spacing with known values instead of
 * ad-hoc `className` overrides. All steps keep a comfortable `p-4` on small
 * screens and scale up on `lg`.
 */
export type PaperPadding = "none" | "xs" | "sm" | "md" | "lg" | "xl"

const paddingClasses: Record<PaperPadding, string> = {
  none: "p-0",
  xs: "p-3",
  sm: "p-4",
  md: "p-4 lg:p-6",
  lg: "p-4 lg:p-8",
  xl: "p-4 lg:p-10",
}

/**
 * Preset steps for the vertical spacing between the paper's children.
 */
export type PaperGap = "none" | "sm" | "md" | "lg"

const gapClasses: Record<PaperGap, string> = {
  none: "gap-0",
  sm: "gap-2",
  md: "gap-3",
  lg: "gap-4",
}

/**
 * Cross-axis alignment of the children within the paper.
 * - `center`: horizontally and vertically centered (the default)
 * - `start`: aligned to the start (top-left)
 * - `stretch`: children keep their natural width, no centering
 */
export type PaperAlign = "center" | "start" | "stretch"

const alignClasses: Record<PaperAlign, string> = {
  center: "items-center justify-center",
  start: "items-start",
  stretch: "",
}

type Props = PropsWithChildren<{
  id?: string

  /**
   * Escape hatch for *outer layout only* — margin, width, max-width and
   * positioning that describe how the paper sits in its parent. Do NOT use it
   * for padding, gap, alignment, radius or background; those are owned by the
   * `padding`, `gap`, `align`, `shape` and `background` props respectively.
   */
  className?: string

  /**
   * Inner padding preset.
   * @default "xl"
   */
  padding?: PaperPadding

  /**
   * Vertical spacing between the children.
   * @default "sm"
   */
  gap?: PaperGap

  /**
   * Cross-axis alignment of the children.
   * @default "center"
   */
  align?: PaperAlign

  /**
   * Background color. Forwarded to `PaperBox`.
   */
  background?: BackgroundDesignator

  /**
   * Corner radius. Forwarded to `PaperBox`.
   * @default "border/shape/16"
   */
  shape?: BorderShape

  /**
   *  Elevates the card with a drop shadow while hovered. Forwarded to
   *  `PaperBox`.
   */
  elevateOnHover?: boolean
}>

/**
 * Wraps child components. It adds padding and a white background (et cetera)
 * similar to being on paper. Composes `PaperBox` for the border/background
 * chrome and layers on flex, padding, gap, and cross-axis alignment. Layout is
 * controlled through the `padding`, `gap` and `align` presets rather than raw
 * Tailwind classes.
 */
export const Paper = ({
  children,
  className,
  padding = "xl",
  gap = "sm",
  align = "center",
  background,
  shape,
  id,
  elevateOnHover,
}: Props) => (
  <PaperBox
    id={id}
    background={background}
    shape={shape}
    elevateOnHover={elevateOnHover}
    className={classNames("flex flex-col", gapClasses[gap], paddingClasses[padding], alignClasses[align], className)}
  >
    {children}
  </PaperBox>
)
